/**
 * Defensive monkey-patch for `Node.prototype.removeChild` and
 * `Node.prototype.insertBefore` to be tolerant of browser translation
 * extensions that wrap text nodes in `<font>` elements (Chrome translate,
 * Google Translate, etc.).
 *
 * Without this patch, React's commit-phase reconciliation throws
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'` whenever a
 * translated text node is unmounted — Sentry CUSTOMER-UI-32. The patch
 * makes the DOM operations resilient: if the actual parent of the node
 * differs from the expected parent, the operation is performed on the
 * actual parent instead.
 *
 * This is a well-known mitigation that ships in libraries like
 * `react-translation-tolerant`. Trade-offs:
 *
 * - Patches global DOM API. Any later code calling `removeChild` /
 *   `insertBefore` benefits from the fix. Negligible performance cost
 *   (one extra equality check per call).
 * - Could in principle mask a real bug where React tries to remove the
 *   wrong child for a non-translation reason. In practice React's
 *   reconciler is correct; the only common cause of this mismatch is
 *   external DOM mutation, and silently recovering is the right
 *   behavior.
 *
 * Loaded eagerly from `instrumentation-client.ts` so the patch is
 * active before the first React render.
 *
 * See `.claude/rules/web/translation-tolerance.md`.
 */

declare global {
  interface Window {
    /** Set once the DOM patch has been installed. */
    __translationDomPatchInstalled?: boolean;
  }
}

export function installTranslationDomPatch(): void {
  if (typeof window === "undefined" || typeof Node === "undefined") {
    // SSR / non-browser environment — skip.
    return;
  }
  if (window.__translationDomPatchInstalled) {
    return;
  }
  window.__translationDomPatchInstalled = true;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const originalReplaceChild = Node.prototype.replaceChild;

  // Only swallow the specific DOMException the translation race produces.
  // HierarchyRequestError, NotSupportedError, WrongDocumentError, and any
  // genuine React reconciliation bug should still surface — they indicate
  // real problems that would otherwise be hidden behind the patch.
  const isTranslationCrash = (err: unknown): boolean =>
    err instanceof Error && err.name === "NotFoundError";

  // ── removeChild ────────────────────────────────────────────────────
  // If `child.parentNode` is not `this`, translation has rewritten the
  // parent chain. Remove from the actual parent instead. If the node is
  // already detached, succeed silently. The expected-parent path is also
  // wrapped in try/catch — a race between the parent check and the call
  // can still surface a NotFoundError, and the invariant we want is
  // "never let removeChild escape the commit phase".
  Node.prototype.removeChild = function patchedRemoveChild<T extends Node>(
    this: Node,
    child: T
  ): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        try {
          return originalRemoveChild.call(child.parentNode, child) as T;
        } catch (err) {
          if (isTranslationCrash(err)) return child;
          throw err;
        }
      }
      return child;
    }
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (err) {
      if (isTranslationCrash(err)) return child;
      throw err;
    }
  };

  // ── insertBefore ───────────────────────────────────────────────────
  // If the reference node has been moved out of `this` by translation,
  // append at the end instead of throwing.
  Node.prototype.insertBefore = function patchedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      try {
        return originalInsertBefore.call(this, newNode, null) as T;
      } catch (err) {
        if (isTranslationCrash(err)) return newNode;
        throw err;
      }
    }
    try {
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    } catch (err) {
      if (isTranslationCrash(err)) return newNode;
      throw err;
    }
  };

  // ── replaceChild ───────────────────────────────────────────────────
  // React's reconciler uses replaceChild during key-swap reorders. Same
  // pattern: if the old child's actual parent has been rewritten,
  // perform the replace on the actual parent. Fall back to a manual
  // remove+append if the original throws.
  Node.prototype.replaceChild = function patchedReplaceChild<T extends Node>(
    this: Node,
    newChild: Node,
    oldChild: T
  ): T {
    const actualParent = oldChild.parentNode;
    if (actualParent && actualParent !== this) {
      try {
        return originalReplaceChild.call(actualParent, newChild, oldChild) as T;
      } catch (err) {
        if (!isTranslationCrash(err)) throw err;
        // NotFoundError fallback: manual remove+append on best-effort basis.
        try {
          actualParent.removeChild(oldChild);
        } catch (removeErr) {
          if (!isTranslationCrash(removeErr)) throw removeErr;
        }
        try {
          this.appendChild(newChild);
        } catch (appendErr) {
          if (!isTranslationCrash(appendErr)) throw appendErr;
        }
        return oldChild;
      }
    }
    try {
      return originalReplaceChild.call(this, newChild, oldChild) as T;
    } catch (err) {
      if (isTranslationCrash(err)) return oldChild;
      throw err;
    }
  };
}
